import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../utils/cn'
import { Loader2 } from 'lucide-react'

const bgColor = {
  ig: 'bg-ig-base',
  line: 'bg-line-base',
  primary: 'bg-button-primary-default',
  secondary: 'bg-white',
  tertiary: 'bg-button-tertiary-default',
  inverse: 'bg-button-inverse-default',
  menu: 'bg-button-primary-default',
}

const textColor = {
  ig: 'text-white',
  line: 'text-white',
  primary: 'text-button-primary-text',
  secondary: 'text-button-secondary-content-default',
  tertiary: 'text-content-default',
  inverse: 'text-button-inverse-content-default',
  menu: 'text-content-default',
}

const buttonVariants = cva(
  'gap-3 hover:gap-1 py-0 inline-flex items-center justify-center whitespace-nowrap rounded-button disabled:pointer-events-none',
  {
    variants: {
      variant: {
        ig: 'bg-ig-base text-white hover:bg-ig-hover focus:bg-ig-active',
        line: 'bg-line-base text-white hover:bg-line-hover focus:bg-line-active',
        primary:
          'bg-button-primary-default text-button-primary-text hover:bg-button-primary-hover focus:bg-button-primary-active disabled:bg-button-primary-disabled disabled:text-button-primary-text-disabled',
        secondary:
          'border-[0.125rem] hover:border-[0.188rem] border-button-secondary-stroke-default hover:border-button-secondary-stroke-hover text-button-secondary-content-default hover:text-button-secondary-content-hover focus:bg-button-secondary-active focus:text-button-secondary-content-active focus:border-button-secondary-active',
        tertiary:
          'bg-button-tertiary-default text-content-default hover:bg-button-tertiary-hover hover:text-button-tertiary-content-hover focus:bg-button-tertiary-active focus:text-button-tertiary-content-active disabled:bg-tertiary-default disabled:text-content-disabled',
        inverse:
          'bg-button-inverse-default text-button-inverse-content-default hover:bg-button-inverse-hover hover:text-button-inverse-content-hover focus:bg-button-inverse-active focus:text-button-inverse-content-active',
        menu: 'text-content-default hover:bg-overlay-ghost-hover hover:text-button-tertiary-content-hover disabled:bg-tertiary-default disabled:text-content-disabled',
      },
      size: {
        lg: 'text-lg h-button-lg px-6 hover:px-7',
        md: 'text-md h-button-md px-5 hover:px-6',
        sm: 'text-sm h-button-sm px-4 hover:px-5',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
)

type ButtonSize = 'sm' | 'md' | 'lg'
export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  size?: ButtonSize
  asChild?: boolean
  isLoading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

const ButtonWithIcon = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size = 'md',
      asChild = false,
      leftIcon,
      rightIcon,
      isLoading,
      ...props
    },
    ref
  ) => {
    const Comp = 'button'
    const selectedVariant = variant || 'primary'
    return (
      <div className='relative'>
        <Comp
          className={cn(
            buttonVariants({
              variant,
              size,
              className: `${
                isLoading ? `${bgColor[selectedVariant]} text-transparent` : ''
              } ${className}`,
            })
          )}
          ref={ref}
          {...props}
        >
          <Loader2
            className={`${
              isLoading ? 'animate-spin' : 'hidden'
            } absolute m-auto ${textColor[selectedVariant]}`}
          />
          {leftIcon && leftIcon}
          {props.children}
          {rightIcon && rightIcon}
        </Comp>
      </div>
    )
  }
)
ButtonWithIcon.displayName = 'Button'

export { ButtonWithIcon, buttonVariants }
